import React from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";

interface Props {
  title: string;
  signerName: string | null;
  error: string | null;
  busy: boolean;
  codeSent: boolean;
  code: string;
  onCodeChange: (code: string) => void;
  onSend: () => void;
  onContinue: () => void;
  /** What the code covers, under the heading ("To confirm it is you, …"). */
  prompt?: string;
}

/**
 * The emailed-code step of the public sign page: one shared implementation for a single link (SignPage) and a
 * batch link (BatchSignPage), so both read and behave the same. The page owns the calls and the state.
 */
const CodeStep: React.FC<Props> = ({
  title,
  signerName,
  error,
  busy,
  codeSent,
  code,
  onCodeChange,
  onSend,
  onContinue,
  prompt = "To confirm it is you, we will email you a 6-digit code.",
}) => (
  <Box p={{ xs: 2, md: 4 }} maxWidth={480} mx="auto">
    <Typography variant="h5" component="h1" gutterBottom>
      {title}
    </Typography>
    {signerName && (
      <Typography color="text.secondary" gutterBottom>
        For {signerName}
      </Typography>
    )}
    {error && (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    )}
    <Stack spacing={2} mt={2}>
      <Typography>{prompt}</Typography>
      <Button variant="outlined" disabled={busy} onClick={onSend}>
        Email me a code
      </Button>
      {codeSent && (
        <>
          <TextField
            label="6-digit code"
            value={code}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ""))}
          />
          <Button variant="contained" disabled={busy || code.length !== 6} onClick={onContinue}>
            Continue
          </Button>
        </>
      )}
    </Stack>
  </Box>
);

export default CodeStep;
